import type { StorageManager } from '@/storage/save-data';
import type { SyncManager } from '@/sync/sync-manager';
import type { LevelData } from '@/game/level';
import type { Verifier } from '@/sync';
import { downloadLevel, remixLevel as remixLevelAction } from '@/sync/sync-actions';
import { toast } from '@/ui/toast';
import { CompletionListModal } from '@/ui/completion-list-modal';

interface BrowseCard {
  remoteId: string;
  slug: string;
  name: string;
  description: string;
  difficulty: string;
  modeFlags: number;
  tags: string[];
  ownerDisplayName: string;
  ownerId: string;
  version: number;
  sizeBytes: number;
  downloadCount: number;
  remixOf: string | null;
  updatedAt: number;
  verifier: Verifier | null;
  currentVersionCompletionCount: number;
}

type Sort = 'newest' | 'popular' | 'updated';
const DIFFICULTIES = ['easy', 'normal', 'hard', 'insane', 'triggered'] as const;
const MODES = ['real', 'complex', 'mixed'] as const;

export class BrowseScreen {
  private element: HTMLElement | null = null;
  private items: BrowseCard[] = [];
  private nextCursor: string | null = null;
  private sort: Sort = 'newest';
  private selectedDifficulties: Set<string> = new Set();
  private selectedMode: (typeof MODES)[number] | null = null;
  private authorFilter: string | null = null;
  private loading = false;
  private includeUnverified = false;
  private completionsModal = new CompletionListModal();

  onBack: (() => void) | null = null;
  onPlay: ((level: LevelData) => void) | null = null;

  constructor(private storage: StorageManager, private syncManager: SyncManager) {}

  show(authorFilter: string | null = null): void {
    this.authorFilter = authorFilter;
    this.items = [];
    this.nextCursor = null;
    this.render();
    this.fetch(/*append=*/ false);
  }

  hide(): void {
    if (this.element) { this.element.remove(); this.element = null; }
  }

  private async fetch(append: boolean): Promise<void> {
    this.loading = true;
    this.updateLoadMoreButton();
    try {
      const result = await this.syncManager.browseLevels({
        sort: this.sort,
        difficulties: this.selectedDifficulties.size > 0 ? Array.from(this.selectedDifficulties) : undefined,
        mode: this.selectedMode ?? undefined,
        author: this.authorFilter ?? undefined,
        cursor: append ? this.nextCursor ?? undefined : undefined,
        limit: 20,
        includeUnverified: this.includeUnverified,
      });
      const newItems = result.items as BrowseCard[];
      this.items = append ? [...this.items, ...newItems] : newItems;
      this.nextCursor = result.nextCursor;
    } catch (e: unknown) {
      const err = e as { name?: string };
      if (err.name !== 'NotAuthenticatedError') {
        toast("Can't reach the server. Check your connection and try again.", { variant: 'error' });
      }
    } finally {
      this.loading = false;
      this.renderList();
      this.updateLoadMoreButton();
    }
  }

  private render(): void {
    if (this.element) this.element.remove();
    const el = document.createElement('div');
    el.id = 'browse-screen';
    el.style.cssText = `
      position: fixed; inset: 0;
      background: rgba(10, 10, 26, 0.95);
      display: flex; flex-direction: column;
      align-items: center; padding: 2vh 16px;
      z-index: 60; backdrop-filter: blur(8px);
      font-family: 'Courier New', monospace; color: #ccc;
      overflow-y: auto;
    `;

    const authorHeader = this.authorFilter
      ? `<div style="background:#111;border:1px solid #333;padding:6px 12px;border-radius:4px;margin-bottom:8px;display:flex;align-items:center;gap:8px">
          <span style="color:#888;font-size:0.75rem">Filtered by author</span>
          <button id="bs-clear-author" style="background:#00ffff22;border:1px solid #00ffff;color:#00ffff;padding:3px 8px;font:inherit;font-size:0.7rem;border-radius:3px;cursor:pointer">&larr; all levels</button>
         </div>`
      : '';

    el.innerHTML = `
      <div style="color:#00ffff;font-size:clamp(1.2rem,4vw,1.6rem);font-weight:bold;
                  text-shadow:0 0 20px #00ffff;letter-spacing:0.15em;margin-bottom:0.8rem">
        BROWSE LEVELS
      </div>
      ${authorHeader}
      <div class="bs-filters" style="background:#111;border:1px solid #333;border-radius:6px;padding:8px;margin-bottom:12px;max-width:560px;width:100%;display:flex;flex-wrap:wrap;gap:6px;align-items:center">
        ${this.renderSortGroup()}
        <span style="width:1px;height:16px;background:#333;margin:0 4px"></span>
        ${this.renderDifficultyGroup()}
        <span style="width:1px;height:16px;background:#333;margin:0 4px"></span>
        ${this.renderModeGroup()}
        <span style="width:1px;height:16px;background:#333;margin:0 4px"></span>
        ${this.renderUnverifiedChip()}
      </div>
      <div class="bs-list" style="flex:1;overflow:auto;width:100%;max-width:560px;display:flex;flex-direction:column;gap:8px"></div>
      <div style="text-align:center;margin-top:14px"><button id="bs-loadmore" style="background:#111;color:#00ffff;border:1px solid #00ffff44;padding:8px 24px;font:inherit;font-size:0.8rem;border-radius:4px;cursor:pointer">LOAD MORE</button></div>
      <div style="text-align:center;margin-top:16px"><button id="bs-back" style="background:#111;color:#aaa;border:1px solid #333;padding:8px 20px;font:inherit;border-radius:4px;cursor:pointer">BACK</button></div>
    `;
    document.body.appendChild(el);
    this.element = el;

    el.querySelectorAll<HTMLElement>('[data-filter]').forEach((b) => {
      b.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        this.onFilterClick(b.dataset.filter!, b.dataset.value!);
      });
    });
    el.querySelector('#bs-back')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation(); this.hide(); this.onBack?.();
    });
    el.querySelector('#bs-loadmore')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (this.nextCursor && !this.loading) this.fetch(true);
    });
    el.querySelector('#bs-clear-author')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation(); this.authorFilter = null; this.items = []; this.nextCursor = null;
      this.render(); this.fetch(false);
    });

    this.renderList();
    this.updateLoadMoreButton();
  }

  private renderSortGroup(): string {
    const sorts: [Sort, string][] = [['newest', 'Newest'], ['popular', 'Popular'], ['updated', 'Updated']];
    return `<span style="color:#666;font-size:0.7rem;margin-right:4px">SORT:</span>` +
      sorts.map(([k, label]) => this.chip('sort', k, label, this.sort === k)).join('');
  }
  private renderDifficultyGroup(): string {
    return `<span style="color:#666;font-size:0.7rem">DIFFICULTY:</span>` +
      DIFFICULTIES.map((d) => this.chip('difficulty', d, d, this.selectedDifficulties.has(d))).join('');
  }
  private renderModeGroup(): string {
    return `<span style="color:#666;font-size:0.7rem">MODE:</span>` +
      MODES.map((m) => this.chip('mode', m, m, this.selectedMode === m)).join('');
  }

  private renderUnverifiedChip(): string {
    const active = this.includeUnverified;
    const bg = active ? '#00ffff22' : '#111';
    const color = active ? '#00ffff' : '#888';
    const border = active ? '#00ffff' : '#333';
    return `<span data-filter="includeUnverified" data-value="1" style="background:${bg};border:1px solid ${border};color:${color};padding:3px 8px;border-radius:12px;font-size:0.7rem;cursor:pointer;white-space:nowrap">Show unverified</span>`;
  }

  private chip(filter: string, value: string, label: string, active: boolean): string {
    const bg = active ? '#00ffff22' : '#111';
    const color = active ? '#00ffff' : '#888';
    const border = active ? '#00ffff' : '#333';
    return `<span data-filter="${filter}" data-value="${value}" style="background:${bg};border:1px solid ${border};color:${color};padding:3px 8px;border-radius:12px;font-size:0.7rem;cursor:pointer;white-space:nowrap">${escapeHtml(label)}</span>`;
  }

  private onFilterClick(filter: string, value: string): void {
    if (filter === 'sort') this.sort = value as Sort;
    else if (filter === 'difficulty') {
      if (this.selectedDifficulties.has(value)) this.selectedDifficulties.delete(value);
      else this.selectedDifficulties.add(value);
    } else if (filter === 'mode') {
      this.selectedMode = this.selectedMode === value ? null : (value as (typeof MODES)[number]);
    } else if (filter === 'includeUnverified') {
      this.includeUnverified = !this.includeUnverified;
    }
    this.items = []; this.nextCursor = null;
    this.render();
    this.fetch(false);
  }

  private renderList(): void {
    if (!this.element) return;
    const list = this.element.querySelector('.bs-list') as HTMLElement | null;
    if (!list) return;
    if (this.items.length === 0 && !this.loading) {
      list.innerHTML = `<div style="color:#667;text-align:center;padding:20px 12px">No levels match your filters.</div>`;
      return;
    }
    if (this.loading && this.items.length === 0) {
      list.innerHTML = `<div style="color:#667;text-align:center;padding:20px 12px">Loading\u2026</div>`;
      return;
    }
    list.innerHTML = '';
    for (const item of this.items) {
      const card = document.createElement('div');
      card.style.cssText = `background:rgba(0,255,255,0.03);border:1px solid #00ffff44;border-radius:6px;padding:10px 14px;cursor:pointer`;
      card.dataset.remoteId = item.remoteId;
      const tagHtml = item.tags.slice(0, 4).map((t) =>
        `<span style="background:#333;color:#aaa;padding:2px 6px;border-radius:8px;font-size:0.6rem">${escapeHtml(t)}</span>`,
      ).join(' ');
      const moreTags = item.tags.length > 4 ? `<span style="color:#666;font-size:0.65rem">+${item.tags.length - 4}</span>` : '';
      const remixLine = item.remixOf ? ` \u00b7 remix of <span style="color:#b99aff">source</span>` : '';
      const authorCtrl = `<span data-author="${item.ownerId}" style="color:#00ffff;cursor:pointer">${escapeHtml(item.ownerDisplayName)}</span>`;
      const verifierLine = item.verifier
        ? `<div style="color:#00ffff;font-size:0.7rem">\ud83c\udfc6 verified by ${escapeHtml(item.verifier.displayName)}</div>
           <div style="color:#666;font-size:0.65rem;margin-top:1px">${item.currentVersionCompletionCount} completion${item.currentVersionCompletionCount === 1 ? '' : 's'}</div>`
        : `<div style="color:#cc8a3a;font-size:0.7rem">\u26a0 Unverified</div>`;
      card.innerHTML = `
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px">
          <div style="flex:1;min-width:0">
            <div style="color:#fff;font-size:1rem;font-weight:bold">${escapeHtml(item.name)}</div>
            <div style="color:#888;font-size:0.75rem;margin-top:2px">by ${authorCtrl}${remixLine}</div>
            <div style="display:flex;gap:4px;margin-top:6px;flex-wrap:wrap">${tagHtml} ${moreTags}</div>
          </div>
          <div style="text-align:right;color:#666;font-size:0.7rem;white-space:nowrap">
            ${verifierLine}
            <div style="color:#444;font-size:0.6rem;margin-top:4px">\u2b07 ${item.downloadCount} \u00b7 ${relativeTime(item.updatedAt)}</div>
          </div>
        </div>
      `;
      card.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.dataset.author) {
          e.stopPropagation();
          this.show(target.dataset.author);
          return;
        }
        e.stopPropagation();
        this.openPreview(item);
      });
      list.appendChild(card);
    }
  }

  private updateLoadMoreButton(): void {
    if (!this.element) return;
    const btn = this.element.querySelector('#bs-loadmore') as HTMLButtonElement | null;
    if (!btn) return;
    if (this.loading) { btn.textContent = 'LOADING\u2026'; btn.disabled = true; return; }
    if (!this.nextCursor) { btn.textContent = 'NO MORE'; btn.disabled = true; return; }
    btn.textContent = 'LOAD MORE'; btn.disabled = false;
  }

  private openPreview(card: BrowseCard): void {
    const already = this.storage.getDownloadedLevels().some((l) => l.meta.remoteId === card.remoteId);
    const backdrop = document.createElement('div');
    backdrop.id = 'bs-preview-backdrop';
    backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:70;display:flex;align-items:center;justify-content:center;padding:20px;font-family:"Courier New",monospace';

    const tagHtml = card.tags.map((t) =>
      `<span style="background:#333;color:#aaa;padding:2px 6px;border-radius:8px;font-size:0.65rem">${escapeHtml(t)}</span>`,
    ).join(' ');
    const desc = card.description
      ? `<div style="color:#aaa;font-size:0.85rem;margin-bottom:12px;line-height:1.4">${escapeHtml(card.description)}</div>`
      : '';
    const remixLine = card.remixOf ? ` \u00b7 remix` : '';
    const verifierStrip = card.verifier
      ? `<div style="background:rgba(0,255,255,0.06);border:1px solid #00ffff44;border-radius:4px;padding:8px 12px;margin-bottom:12px;color:#00ffff;font-size:0.75rem">
           \ud83c\udfc6 Verified by ${escapeHtml(card.verifier.displayName)}
           <div style="color:#888;font-size:0.7rem;margin-top:2px">${card.currentVersionCompletionCount} player${card.currentVersionCompletionCount === 1 ? '' : 's'} completed this version</div>
         </div>`
      : `<div style="background:rgba(204,138,58,0.06);border:1px solid #cc8a3a55;border-radius:4px;padding:8px 12px;margin-bottom:12px;color:#cc8a3a;font-size:0.75rem">
           \u26a0 Not yet verified \u2014 be the first!
         </div>`;

    backdrop.innerHTML = `
      <div style="background:#0a0a1a;border:1px solid #00ffff44;border-radius:8px;padding:20px;max-width:380px;width:100%;color:#ccc">
        <div style="color:#00ffff;font-size:1.3rem;font-weight:bold;margin-bottom:4px">${escapeHtml(card.name)}</div>
        <div style="color:#888;font-size:0.85rem;margin-bottom:12px">by <span style="color:#00ffff;cursor:pointer" data-author="${escapeHtml(card.ownerId)}">${escapeHtml(card.ownerDisplayName)}</span>${remixLine}</div>
        ${desc}
        <div style="display:flex;gap:4px;flex-wrap:wrap;margin-bottom:16px">${tagHtml}</div>
        <div style="display:flex;justify-content:space-between;color:#666;font-size:0.7rem;margin-bottom:20px;padding:8px 0;border-top:1px solid #222;border-bottom:1px solid #222">
          <span>\u2b07 ${card.downloadCount} downloads</span>
          <span>${(card.sizeBytes / 1024).toFixed(1)} KB</span>
          <span>${relativeTime(card.updatedAt)}</span>
        </div>
        ${verifierStrip}
        <div style="display:flex;flex-direction:column;gap:8px">
          <button id="bs-download" ${already ? 'disabled' : ''} style="background:#111;border:1px solid ${already ? '#333' : '#00ffff44'};color:${already ? '#666' : '#00ffff'};padding:10px;font:inherit;font-size:0.85rem;border-radius:4px;cursor:${already ? 'default' : 'pointer'}">${already ? '\u2713 Already downloaded' : '\u2b07 Download (to my device)'}</button>
          <button id="bs-remix" style="background:#111;border:1px solid #8855ff;color:#b99aff;padding:10px;font:inherit;font-size:0.85rem;border-radius:4px;cursor:pointer">\u270e Remix (save a copy I can edit)</button>
          <button id="bs-see-completions" style="background:#111;border:1px solid #00ffff44;color:#00ffff;padding:8px;font:inherit;font-size:0.75rem;border-radius:4px;cursor:pointer">\ud83d\udc65 See all completions</button>
          <button id="bs-see-author" style="background:#111;border:1px solid #00ffff44;color:#666;padding:8px;font:inherit;font-size:0.8rem;border-radius:4px;cursor:pointer">by ${escapeHtml(card.ownerDisplayName)} \u2014 see more \u2192</button>
        </div>
        <div style="text-align:center;margin-top:12px"><button id="bs-close" style="background:none;border:none;color:#666;font:inherit;font-size:0.75rem;cursor:pointer">Close</button></div>
      </div>
    `;
    document.body.appendChild(backdrop);

    const close = () => backdrop.remove();
    backdrop.addEventListener('pointerdown', (e) => {
      if (e.target === backdrop) close();
    });
    backdrop.querySelector('#bs-close')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation(); close();
    });
    backdrop.querySelector('#bs-download')?.addEventListener('pointerdown', async (e) => {
      e.stopPropagation();
      if (already) return;
      close();
      await downloadLevel(card.remoteId, { storage: this.storage, syncManager: this.syncManager });
    });
    backdrop.querySelector('#bs-remix')?.addEventListener('pointerdown', async (e) => {
      e.stopPropagation();
      close();
      await remixLevelAction(card.remoteId, { storage: this.storage, syncManager: this.syncManager });
    });
    backdrop.querySelector('#bs-see-author')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      close();
      this.show(card.ownerId);
    });
    backdrop.querySelector('#bs-see-completions')?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.completionsModal.show(card.name, card.remoteId, async (cursor) => {
        return this.syncManager.listCompletions(card.remoteId, { cursor, limit: 20 });
      });
    });
    const authorLink = backdrop.querySelector('[data-author]') as HTMLElement | null;
    authorLink?.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      close();
      this.show(authorLink!.dataset.author!);
    });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
}

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
